import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type SidebarMode = 'expanded' | 'collapsed' | 'hidden';

interface SidebarContextType {
  mode: SidebarMode;
  isHovering: boolean;
  isMobile: boolean;
  isExpanded: boolean;
  isCollapsed: boolean;
  isHidden: boolean;
  shouldShowSidebar: boolean;
  sidebarWidth: string;
  setMode: (mode: SidebarMode) => void;
  toggleMode: () => void;
  cycleMode: () => void;
  setHovering: (hovering: boolean) => void;
  setMobile: (mobile: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const STORAGE_KEY = 'sidebar-mode';

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<SidebarMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && ['expanded', 'collapsed', 'hidden'].includes(saved)) {
      return saved as SidebarMode;
    }
    return 'expanded';
  });

  const [isHovering, setIsHovering] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Computed values
  const isExpanded = mode === 'expanded';
  const isCollapsed = mode === 'collapsed';
  const isHidden = mode === 'hidden';

  const shouldShowSidebar = (() => {
    if (isMobile) return true; // Mobile always shows sidebar when opened
    if (mode === 'hidden' && isHovering) return true;
    if (mode !== 'hidden') return true;
    return false;
  })();

  const sidebarWidth = (() => {
    if (mode === 'hidden' && !isHovering) return '0px';
    if (mode === 'collapsed' && !isHovering) return '60px';
    return '260px';
  })();

  // Actions
  const setMode = (newMode: SidebarMode) => {
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  };

  const toggleMode = () => {
    const modes: SidebarMode[] = ['expanded', 'collapsed', 'hidden'];
    const currentIndex = modes.indexOf(mode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setMode(modes[nextIndex]);
  };

  const cycleMode = () => {
    if (mode === 'expanded') {
      setMode('collapsed');
    } else if (mode === 'collapsed') {
      setMode('hidden');
    } else {
      setMode('expanded');
    }
  };

  const setHovering = (hovering: boolean) => {
    setIsHovering(hovering);
  };

  const setMobile = (mobile: boolean) => {
    setIsMobile(mobile);
  };

  // Watch for mobile changes
  useEffect(() => {
    if (isMobile && mode === 'hidden') {
      setMode('collapsed');
    }
  }, [isMobile, mode]);

  const value: SidebarContextType = {
    mode,
    isHovering,
    isMobile,
    isExpanded,
    isCollapsed,
    isHidden,
    shouldShowSidebar,
    sidebarWidth,
    setMode,
    toggleMode,
    cycleMode,
    setHovering,
    setMobile,
  };

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
